import type { Material } from "@/types/Views/MaterialDatabase/Material";
import type { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";

import type { ColourKey, ColourKeyCategory } from "./colourKeys";
import { includedLayers, layerCountSubLine } from "./keyLayers";

export type EpdQuality = "unmapped" | "generic" | "custom" | "industry" | "product";

// Worst first: a type painted by its layers takes the earliest category any of them has.
export const EPD_QUALITY_WORST_FIRST: EpdQuality[] = ["unmapped", "generic", "custom", "industry", "product"];

// Scene colours, mirroring the epd-quality-* tokens in style.css; kept off the Material mapping tool's green/yellow/red.
export const EPD_QUALITY_COLOURS: Record<EpdQuality, string> = {
  product: "#0f766e",
  industry: "#5eb8ad",
  custom: "#a072da",
  generic: "#e9c46a",
  unmapped: "#475569",
};

export const EPD_QUALITY_SWATCH_CLASSES: Record<EpdQuality, string> = {
  product: "bg-epd-quality-product",
  industry: "bg-epd-quality-industry",
  custom: "bg-epd-quality-custom",
  generic: "bg-epd-quality-generic",
  unmapped: "bg-epd-quality-unmapped",
};

export const EPD_QUALITY_LABELS: Record<EpdQuality, string> = {
  product: "Product EPD",
  industry: "Industry EPD",
  custom: "Custom data",
  generic: "Generic data",
  unmapped: "Not mapped",
};

// Mirrors EpdProductIndustryType in the API: INDUSTRY = 0, PRODUCT = 1, GENERIC = 2.
const BY_PRODUCT_INDUSTRY_TYPE: Record<number, EpdQuality> = { 0: "industry", 1: "product", 2: "generic" };

const rank = (quality: EpdQuality) => EPD_QUALITY_WORST_FIRST.indexOf(quality);

export const worseOf = (first: EpdQuality, second: EpdQuality): EpdQuality => (rank(second) < rank(first) ? second : first);

// A construction or an unclassified record counts as custom unless it carries a known EPD type.
export function layerQuality(mapping: Material | null | undefined): EpdQuality {
  if (!mapping) return "unmapped";
  const known = mapping.epdInfo ? BY_PRODUCT_INDUSTRY_TYPE[mapping.epdInfo.epdProductIndustryType] : undefined;
  if (mapping.isCustom && mapping.isConstruction !== true) return "custom";
  return known ?? "custom";
}

// Null when nothing in the type counts. With layers, only their flags decide: the API copies the FIRST layer's
// includeMapping onto the type, so an excluded first layer would otherwise hide a type that still has included ones.
export function typeQuality(type: SpeckleMaterial): EpdQuality | null {
  if (!type.children?.length) return type.includeMapping === false ? null : layerQuality(type.mapping);
  const layers = includedLayers(type);
  if (!layers.length) return null;
  return layers.map((layer) => layerQuality(layer.mapping)).reduce(worseOf);
}

const EPD_QUALITY_CATEGORIES: ColourKeyCategory[] = EPD_QUALITY_WORST_FIRST.map((quality) => ({
  id: quality,
  label: EPD_QUALITY_LABELS[quality],
  colour: EPD_QUALITY_COLOURS[quality],
  swatchClass: EPD_QUALITY_SWATCH_CLASSES[quality],
}));

const SUB_LINE_WORDING: Record<string, string> = {
  unmapped: "mapped",
  generic: "generic",
  custom: "custom",
  industry: "on industry EPDs",
  product: "on product EPDs",
};

export function epdSubLine(type: SpeckleMaterial, category: string): string {
  return layerCountSubLine(type, category, (layer) => layerQuality(layer.mapping), SUB_LINE_WORDING);
}

export const EPD_QUALITY_KEY = {
  id: "epdQuality" as const,
  label: "EPD quality",
  group: "epd",
  hint: "Each type shows the weakest data among its material layers.",
  categoriesFor: () => EPD_QUALITY_CATEGORIES,
  trackCategory: "unmapped",
  headline: {
    goodCategories: ["product", "industry"],
    text: (good: number, total: number) => `${good} / ${total} elements on product or industry EPDs`,
  },
  classifyLayer: (layer: SpeckleMaterial) => layerQuality(layer.mapping),
  classifyType: typeQuality,
  subLine: epdSubLine,
} satisfies ColourKey;
